import React, { useContext } from "react";
import SettingsIcon from "@material-ui/icons/Settings";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import CancelIcon from "@material-ui/icons/Cancel";
import ReplayIcon from "@material-ui/icons/Replay";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import TestSeriesSearchResult from "../../../api/models/test-series/TestSeriesSearchResult";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import TestPlanPermissionContext from "../../../contexts/TestPlanPermissionContext";
import { TestSeriesPermissions } from "../../../api/services/PermissionApi";
import ReusedTestSeriesSearchResult from "../../../api/models/test-series/ReusedTestSeriesSearchResult";
import { dashToSlash } from "../../../api/services/common/FormatDateService";
import { testLocationDictionary } from "../../../api/models/TestLocation";
import WithFlagIconsList from "../../../components/Flags/WithFlagIconsList";

const DEFAULT_TEST_PLAN_NAME = "User Defined Test Plan";
const REUSED_TEST_PLAN_NAME = "Historical Data";

type TestSeries<IsReused> = IsReused extends true ? ReusedTestSeriesSearchResult : TestSeriesSearchResult;

interface DataRow {
  testMethod: string;
  note: string;
  status: string;
  id: number;
  rowNumber: number;
  testCasesGenerated: boolean;
}

interface TestSeriesOverviewTableProps<IsReused extends boolean, TS extends TestSeries<IsReused>> {
  data: TS[];
  isReused: IsReused;
  onOpenTestConfiguration: (id: number, version: number, isReused: boolean, originRequestId?: string) => void;
  onOpenTestOverview: (id: number, version: number, isReused: boolean, originRequestId?: string) => void;
  onDelete: (row: DataRow) => void;
  onCancel: (row: DataRow) => void;
  onDuplicate: (id: number) => void;
  onReopen: (id: number) => void;
  onMoveToDraft: (row: DataRow) => void;
  testPlanName: string | null;
  withoutSpecification?: boolean;
  onOpenMatrixExportConfiguration: (toggle: boolean, id: number, version: number) => void;
}

function TestSeriesOverviewTable<IsReused extends boolean, TS extends TestSeries<IsReused>>(
  props: TestSeriesOverviewTableProps<IsReused, TS>,
) {
  const {
    data,
    isReused,
    onOpenTestConfiguration,
    onOpenTestOverview,
    onDelete,
    onCancel,
    onDuplicate,
    onReopen,
    onMoveToDraft,
    testPlanName,
    withoutSpecification,
    onOpenMatrixExportConfiguration,
  } = props;

  const header = testPlanName || (withoutSpecification && DEFAULT_TEST_PLAN_NAME) || REUSED_TEST_PLAN_NAME;
  const {
    accessProgress,
    reusedTestSeries: reusedTestSeriesPermissions,
    testSeries: testSeriesPermissions,
  } = useContext(TestPlanPermissionContext);

  const mapToReusedRowData = (ts: ReusedTestSeriesSearchResult) => ({
    visibleId: `${ts.requestId}-${ts.indexNumber}`,
    testMethod: (
      <WithFlagIconsList flags={ts.modified ? ["MODIFIED"] : []} tooltipForChildren={ts.testMethodName}>
        {ts.testMethodName}
      </WithFlagIconsList>
    ),
    note: ts.note,
    completedAt: dashToSlash(ts.completedAt),
    id: ts.id,
    testCasesGenerated: ts?.testCasesCount && ts.testCasesCount > 0,
    version: 1,
    originRequestId: ts.requestId,
  });

  const mapToRegularRowData = (ts: TestSeriesSearchResult) => {
    return {
      visibleId: ts.indexNumber,
      testMethod: (
        <WithFlagIconsList flags={ts.modified ? ["MODIFIED"] : []} tooltipForChildren={ts.testMethodName}>
          {ts.testMethodName}
        </WithFlagIconsList>
      ),
      note: ts.note,
      testLocation: testLocationDictionary[ts.testLocation],
      status: formatIdentifierAsString(ts.state),
      progress: ts.progress,
      id: ts.id,
      linkedParent: ts.linkedParent ? ts.linkedParent : null,
      testCasesGenerated: ts.testCasesCount > 0,
      version: ts.version,
    };
  };
  const generateRows = (): CustomTableRowData[] =>
    data.map((ts) =>
      isReused
        ? mapToReusedRowData(ts as ReusedTestSeriesSearchResult)
        : mapToRegularRowData(ts as TestSeriesSearchResult),
    );

  const getColumns = () => {
    const baseColumns = ["ID", "Test Method", "Note"];
    if (isReused) {
      return baseColumns.concat(["Completed"]);
    }
    return baseColumns.concat(accessProgress ? ["Test Location", "Status", "Progress"] : ["Test Location", "Status"]);
  };

  const getColumnWidths = () => {
    const withoutProgressColumn = isReused ? ["110px", "40%", "40%", "20%"] : ["30px", "40%", "40%", "20%"];
    const withProgressColumn = ["30px", "30%", "30%", "15%", "15%", "10%"];
    return accessProgress && !isReused ? withProgressColumn : withoutProgressColumn;
  };

  const allowedTo = (id: number, permission: (permissions: TestSeriesPermissions) => boolean | undefined): boolean => {
    return isReused
      ? (reusedTestSeriesPermissions && permission(reusedTestSeriesPermissions)) || false
      : (testSeriesPermissions?.[id] && permission(testSeriesPermissions[id])) || false;
  };

  return (
    <CustomTable
      header={header}
      hasFixedColumns
      boldIndex={1}
      columns={getColumns()}
      columnExactWidths={getColumnWidths()}
      excludedKeys={["id", "linkedParent", "testCasesGenerated", "version", "isReused", "originRequestId"].concat(
        accessProgress ? [] : ["progress"],
      )}
      formatDates
      rowMenus
      rowMenuActions={[
        {
          action: (rowData): void =>
            onOpenTestConfiguration(rowData.id, rowData.version, isReused, rowData.originRequestId),
          icon: <SettingsIcon />,
          text: "Test Configuration",
          show: (rowData) =>
            rowData.status === "Draft" && allowedTo(rowData.id, (permissions) => permissions.accessTestConfigurator),
        },
        {
          action: (rowData): void => {
            onOpenMatrixExportConfiguration(true, rowData.id, rowData.version);
          },
          icon: <SettingsIcon />,
          text: "Configure Test Series Export",
          show: (rowData): boolean =>
            rowData.testCasesGenerated && allowedTo(rowData.id, (permissions) => permissions.accessTestcases),
        },
        {
          action: (rowData): void => onOpenTestOverview(rowData.id, rowData.version, isReused, rowData.originRequestId),
          icon: <PlayCircleFilledIcon />,
          text: "Tests Overview",
          show: (rowData): boolean =>
            rowData.testCasesGenerated && allowedTo(rowData.id, (permissions) => permissions.accessTestcases),
        },
        {
          action: (rowData): void => onDuplicate(rowData.id),
          icon: <FileCopyIcon />,
          text: "Duplicate",
          show: (rowData): boolean => allowedTo(rowData.id, (permissions) => permissions.duplicate),
        },
        {
          action: (rowData): void => onMoveToDraft(rowData as DataRow),
          icon: <ReplayIcon />,
          text: "Set Test Series to Draft",
          show: (rowData): boolean => allowedTo(rowData.id, (permissions) => permissions.moveToDraft),
        },
        {
          action: (rowData): void => onDelete(rowData as DataRow),
          icon: <DeleteIcon />,
          text: isReused ? "Remove" : "Delete",
          show: (rowData) => allowedTo(rowData.id, (permissions) => permissions.delete),
        },
        {
          action: (rowData): void => onCancel(rowData as DataRow),
          icon: <CancelIcon />,
          text: "Cancel",
          show: (rowData) => allowedTo(rowData.id, (permissions) => permissions.cancel),
        },
        {
          action: (rowData): void => onReopen(rowData.id),
          icon: <ReplayIcon />,
          text: "Reopen",
          show: (rowData) => allowedTo(rowData.id, (permissions) => permissions.reopen),
        },
      ]}
      actionColumnIndexes={[1]}
      handleEdit={(rowData) => {
        if (rowData.status === "Draft") {
          if (allowedTo(rowData.id, (permissions) => permissions.accessTestConfigurator)) {
            onOpenTestConfiguration(rowData.id, rowData.version, isReused, rowData.originRequestId);
          }
        } else if (allowedTo(rowData.id, (permissions) => permissions.accessTestcases)) {
          onOpenTestOverview(rowData.id, rowData.version, isReused, rowData.originRequestId);
        }
      }}
      rows={generateRows()}
    />
  );
}
export default TestSeriesOverviewTable;
